import React from 'react';
import PropTypes from 'prop-types';
import HomeSection from '../HomeSection';
import GearCard from '../../../molecules/GearCard';
import SkillLevelChip from '../../../atoms/SkillLevelChip';
import EditorialEmptyState from '../../../molecules/EditorialEmptyState';
import EditableText from '../../../molecules/EditableText';
import InstrumentExperienceEditor from '../../../molecules/InstrumentExperienceEditor';
import {
  StarIcon,
  AmpIcon,
  KeysIcon,
} from '../../../atoms/icons/EditorialProfileIcons';
import './index.scss';

export default function HomeSoundGearSection({
  id = 'sound',
  eyebrow,
  title,
  side,
  influencesTitle,
  influences = [],
  influencesHint,
  influencesPlaceholder,
  equipmentTitle,
  equipment = [],
  equipmentHint,
  equipmentPlaceholder,
  skillsTitle,
  skills = [],
  instrumentExperienceId,
  emptyTitle,
  emptyDescription,
  editing = false,
  rawInfluences = '',
  rawEquipment = '',
  instrumentExperiences = [],
  onFieldSave,
  onInstrumentExperiencesChange,
}) {
  const hasAnything = influences.length > 0 || equipment.length > 0 || skills.length > 0;

  return (
    <HomeSection
      id={id}
      eyebrow={eyebrow}
      title={title}
      side={side}
      showSide={editing}
      variant="cool"
      className="home-sound"
    >
      {(hasAnything || editing) ? (
        <div className="home-sound__grid">
          <GearCard
            tone="warm"
            icon={<StarIcon width="22" height="22" />}
            title={influencesTitle}
            items={editing ? null : influences}
            hint={editing ? null : influencesHint}
            className={editing ? 'gear-card--editing' : ''}
          >
            {editing ? (
              <EditableText
                editing
                multiline
                rows={4}
                value={rawInfluences || ''}
                placeholder={influencesPlaceholder}
                ariaLabel="Influences"
                maxLength={500}
                onSave={(next) => onFieldSave?.('influences', next)}
                inputClassName="editable-text__textarea--seamless"
              />
            ) : null}
          </GearCard>
          <GearCard
            tone="cool"
            icon={<AmpIcon width="22" height="22" />}
            title={equipmentTitle}
            items={editing ? null : equipment}
            hint={editing ? null : equipmentHint}
            className={editing ? 'gear-card--editing' : ''}
          >
            {editing ? (
              <EditableText
                editing
                multiline
                rows={4}
                value={rawEquipment || ''}
                placeholder={equipmentPlaceholder}
                ariaLabel="Equipment"
                maxLength={1000}
                onSave={(next) => onFieldSave?.('equipment', next)}
                inputClassName="editable-text__textarea--seamless"
              />
            ) : null}
          </GearCard>
          <GearCard
            id={instrumentExperienceId}
            tone="gold"
            icon={<KeysIcon width="22" height="22" />}
            title={skillsTitle}
            className={['home-sound__instrument-card', editing ? 'gear-card--editing' : ''].filter(Boolean).join(' ')}
          >
            {editing ? (
              <InstrumentExperienceEditor
                experiences={instrumentExperiences}
                onChange={onInstrumentExperiencesChange}
                addLabel="Add instrument"
                instrumentPlaceholder="Pick an instrument"
              />
            ) : (
              <div className="gear-card__skills">
                {skills.map((skill, i) => (
                  <div key={skill.id || i} className="gear-card__skill">
                    <div className="gear-card__skill-name">{skill.instrument}</div>
                    <SkillLevelChip level={skill.level}>{skill.level}</SkillLevelChip>
                  </div>
                ))}
              </div>
            )}
          </GearCard>
        </div>
      ) : (
        <EditorialEmptyState
          title={emptyTitle}
          description={emptyDescription}
        />
      )}
    </HomeSection>
  );
}

HomeSoundGearSection.propTypes = {
  id: PropTypes.string,
  eyebrow: PropTypes.node.isRequired,
  title: PropTypes.node.isRequired,
  side: PropTypes.node,
  influencesTitle: PropTypes.node,
  influences: PropTypes.arrayOf(PropTypes.node),
  influencesHint: PropTypes.node,
  influencesPlaceholder: PropTypes.string,
  equipmentTitle: PropTypes.node,
  equipment: PropTypes.arrayOf(PropTypes.node),
  equipmentHint: PropTypes.node,
  equipmentPlaceholder: PropTypes.string,
  skillsTitle: PropTypes.node,
  skills: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    instrument: PropTypes.node,
    level: PropTypes.string,
  })),
  instrumentExperienceId: PropTypes.string,
  emptyTitle: PropTypes.node,
  emptyDescription: PropTypes.node,
  editing: PropTypes.bool,
  rawInfluences: PropTypes.string,
  rawEquipment: PropTypes.string,
  instrumentExperiences: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    instrument: PropTypes.string,
    years: PropTypes.string,
  })),
  onFieldSave: PropTypes.func,
  onInstrumentExperiencesChange: PropTypes.func,
};
